import { BEHAVIOR_OCCURRENCE_SCORE_VALUE } from 'src/constants/programs';

/**
 * A behavior occurrence is stored as an ordinary trial entry on its Behavior Tracking program's data
 * document. `attachedToTarget`
 *
 * @param {Object} data A trial entry, or the payload one is prepared from.
 * @returns {boolean}
 */
export const isBehaviorOccurrence = (data) => Boolean(data?.attachedToTarget);

/**
 * Expands tapped occurrences into one trial entry per tap, grouped by the Behavior Tracking program
 * whose data document each belongs to.
 *
 *
 * @param {Array<{ program: string, target: string, count: number, promptLevel?: string,
 *   scoreValue?: number|null, promptLevelTemplate?: string }>} occurrences
 * @param {string} attachedToTarget The skill target the occurrences were recorded during.
 * @returns {Object<string, Array<Object>>}
 */
export const toBehaviorOccurrenceData = (occurrences, attachedToTarget) => occurrences
  .reduce((byProgram, {
    program, target, count, promptLevel, scoreValue, promptLevelTemplate,
  }) => {
    const entry = {
      target,
      attachedToTarget,
      scoreValue: promptLevel ? (scoreValue ?? null) : BEHAVIOR_OCCURRENCE_SCORE_VALUE,
      ...(promptLevel && { promptLevel }),
      ...(promptLevelTemplate && { promptLevelTemplate }),
    };
    const entries = Array.from({ length: count }, () => ({ ...entry }));

    return { ...byProgram, [program]: [...(byProgram[program] ?? []), ...entries] };
  }, {});

/**
 * Groups occurrences into the squares a Skill Acquisition row shows: one per save, since everything
 * saved together shares a skill target and a moment. Sorted by time.
 *
 * @param {Array<Object>} occurrences Occurrence entries carrying `behaviorProgram`,
 *   `behaviorProgramName`, and `outcome` — the answered prompt level's label, when there is one.
 * @param {Object<string, string>} behaviorLabels Behavior target labels by id.
 * @returns {Array<{ key: string, attachedToTarget: string, time: number, behaviors: Array<Object> }>}
 */
export const toOccurrenceSlots = (occurrences, behaviorLabels) => {
  const slots = new Map();

  occurrences.forEach((occurrence) => {
    const time = occurrence.time ?? 0;
    const key = `${occurrence.attachedToTarget}@${time}`;
    if (!slots.has(key)) {
      slots.set(key, { key, attachedToTarget: occurrence.attachedToTarget, time, behaviors: [] });
    }

    const { behaviors } = slots.get(key);
    const behaviorKey = `${occurrence.behaviorProgram}/${occurrence.target}`;
    let behavior = behaviors.find(({ key: existing }) => existing === behaviorKey);
    if (!behavior) {
      behavior = {
        key: behaviorKey,
        programName: occurrence.behaviorProgramName ?? '',
        label: behaviorLabels[occurrence.target] ?? '',
        count: 0,
        outcomes: [],
      };
      behaviors.push(behavior);
    }
    behavior.count += 1;

    if (occurrence.outcome) {
      const outcome = behavior.outcomes.find(({ label }) => label === occurrence.outcome);
      if (outcome) {
        outcome.count += 1;
      } else {
        behavior.outcomes.push({ label: occurrence.outcome, count: 1 });
      }
    }
  });

  return [...slots.values()].sort((a, b) => a.time - b.time);
};

/**
 * Places occurrence slots among a grid's trial columns by time, as unnumbered columns. A slot follows
 * every trial recorded at or before it, so a behavior noted while scoring a trial sits after it.
 *
 * @param {Array<{ trial: number }>} trialColumns In trial order.
 * @param {Object<number, number>} trialTimes Time of each trial, by trial number.
 * @param {Array<{ key: string, time: number }>} slots Sorted by time.
 * @returns {Array<Object>}
 */
export const interleaveOccurrenceColumns = (trialColumns, trialTimes, slots) => {
  const columns = [];
  let next = 0;

  const slotColumn = (slot) => ({ name: `occurrence-${slot.key}`, label: '', occurrenceSlot: slot });

  trialColumns.forEach((column) => {
    const trialTime = trialTimes[column.trial] ?? 0;
    while (next < slots.length && slots[next].time < trialTime) {
      columns.push(slotColumn(slots[next]));
      next += 1;
    }
    columns.push(column);
  });

  return [...columns, ...slots.slice(next).map(slotColumn)];
};
